package com.rainbowclubs.rainbow.presentation.admin

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.models.Membership
import com.rainbowclubs.rainbow.domain.use_cases.membership.GetExistingMembershipsUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import kotlinx.coroutines.Job
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

class ExistingMembersViewModel(
    private val clubId: String,
    private val getExistingMembershipsUseCase: GetExistingMembershipsUseCase,
) : ViewModel() {

    data class UiState(
        val members: List<Membership> = emptyList(),
        val searchQuery: String = "",
        val nextPage: Int = 1,
        val hasNextPage: Boolean = false,
        val isRefreshing: Boolean = false,
        val refreshError: Boolean = false,
        val isLoadingMore: Boolean = false,
        val loadMoreError: Boolean = false,
    )

    sealed class NavigationEvent {
        object Back : NavigationEvent()
        data class MemberDetails(
            val clubId: String,
            val membershipId: String,
        ) : NavigationEvent()
    }

    private val _screenState = MutableStateFlow<ScreenState<UiState>>(ScreenState.Loading)
    val screenState: StateFlow<ScreenState<UiState>> = _screenState.asStateFlow()

    private val navigationIntentChannel = Channel<NavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<NavigationEvent> = navigationIntentChannel.receiveAsFlow()

    private var searchJob: Job? = null

    init {
        fetchMembers()
    }

    fun fetchMembers() {
        viewModelScope.launch {
            _screenState.value = ScreenState.Loading
            loadFirstPage(searchQuery = "")
        }
    }

    fun onSearchQueryChange(query: String) {
        val currentState = (_screenState.value as? ScreenState.Content)?.data ?: UiState()
        _screenState.value = ScreenState.Content(
            currentState.copy(
                searchQuery = query,
                members = emptyList(),
                nextPage = 1,
                hasNextPage = false,
                isRefreshing = true,
                refreshError = false,
                isLoadingMore = false,
                loadMoreError = false,
            )
        )

        searchJob?.cancel()
        searchJob = viewModelScope.launch {
            delay(SEARCH_DEBOUNCE_MS)
            loadFirstPage(searchQuery = query)
        }
    }

    fun clearSearch() {
        onSearchQueryChange("")
    }

    fun loadNextPage() {
        val currentState = (_screenState.value as? ScreenState.Content)?.data ?: return
        if (currentState.isRefreshing || currentState.isLoadingMore || !currentState.hasNextPage) {
            return
        }

        viewModelScope.launch {
            _screenState.value = ScreenState.Content(
                currentState.copy(
                    isLoadingMore = true,
                    refreshError = false,
                    loadMoreError = false,
                )
            )

            getExistingMembershipsUseCase(
                clubId = clubId,
                page = currentState.nextPage,
                limit = PAGE_SIZE,
                search = currentState.searchQuery.trim().ifBlank { null },
            ).onSuccess { response ->
                val latestState = (_screenState.value as? ScreenState.Content)?.data ?: currentState
                if (latestState.searchQuery != currentState.searchQuery) return@onSuccess

                _screenState.value = ScreenState.Content(
                    latestState.copy(
                        members = latestState.members + response.memberships,
                        nextPage = response.page + 1,
                        hasNextPage = response.hasNextPage,
                        isLoadingMore = false,
                        loadMoreError = false,
                    )
                )
            }.onFailure {
                val latestState = (_screenState.value as? ScreenState.Content)?.data ?: currentState
                if (latestState.searchQuery != currentState.searchQuery) return@onFailure

                _screenState.value = ScreenState.Content(
                    latestState.copy(
                        isLoadingMore = false,
                        loadMoreError = true,
                    )
                )
            }
        }
    }

    fun onBackClick() {
        navigationIntentChannel.trySend(NavigationEvent.Back)
    }

    fun onMemberClick(member: Membership) {
        navigationIntentChannel.trySend(
            NavigationEvent.MemberDetails(
                clubId = clubId,
                membershipId = member.membershipId,
            )
        )
    }

    private suspend fun loadFirstPage(searchQuery: String) {
        getExistingMembershipsUseCase(
            clubId = clubId,
            page = 1,
            limit = PAGE_SIZE,
            search = searchQuery.trim().ifBlank { null },
        ).onSuccess { response ->
            val currentContent = (_screenState.value as? ScreenState.Content)?.data
            if (currentContent != null && currentContent.searchQuery != searchQuery) return@onSuccess

            _screenState.value = ScreenState.Content(
                UiState(
                    members = response.memberships,
                    searchQuery = searchQuery,
                    nextPage = response.page + 1,
                    hasNextPage = response.hasNextPage,
                    refreshError = false,
                )
            )
        }.onFailure {
            val currentContent = (_screenState.value as? ScreenState.Content)?.data
            if (currentContent == null) {
                _screenState.value = ScreenState.Error(it)
            } else if (currentContent.searchQuery == searchQuery) {
                _screenState.value = ScreenState.Content(
                    currentContent.copy(
                        members = emptyList(),
                        isRefreshing = false,
                        refreshError = true,
                        isLoadingMore = false,
                        loadMoreError = false,
                    )
                )
            }
        }
    }

    private companion object {
        const val PAGE_SIZE = 20
        const val SEARCH_DEBOUNCE_MS = 350L
    }
}
